import {Box, Show} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {ComponentPropsWithoutRef, ForwardedRef, forwardRef, useRef, useState} from 'react';

import {isNil, isNotNil, uniqBy} from 'ramda';

import {FOCUSABLE_ELEMENTS_SELECTOR, useDeepCompareEffect, useLatest, useOnMount} from 'shared/web';

import type {TextEditorContentProps} from '../../types/TextEditorContentProps';
import {expandTextEditorSelectionToWords} from '../../utils/expandTextEditorSelectionToWords';
import {getTextareaRangeRects, getTextNodeRects} from './getTextEditorRangeRects';
import {mergeTextEditorRectangles} from './mergeTextEditorRectangles';

interface TextEditorOverlaysProps extends Pick<
  TextEditorContentProps,
  'selectionContent' | 'editorHighlights'
> {
  getEditor: () => HTMLElement | null;
  label?: string | null;
}

interface Rectangle {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The range the editor held before a toolbar control took focus, to paint while it is gone. */
type KeptSelection = Range | {start: number; end: number};

const charAtIn = (text: string) => (index: number) =>
  index < text.length ? (text[index] ?? null) : null;

/**
 * The editor bridges widen a captured selection to whole words before "Rewrite selection" runs,
 * so the kept highlight has to show the same words, not the half the pointer stopped at. Only
 * text-node boundaries are widened; a word never continues across an element boundary here.
 */
const expandRangeToWords = (range: Range): Range => {
  const expanded = range.cloneRange();
  const {startContainer, endContainer} = range;
  if (startContainer instanceof Text) {
    const text = startContainer.data;
    const widened = expandTextEditorSelectionToWords(
      {index: range.startOffset, length: Math.max(1, text.length - range.startOffset)},
      charAtIn(text)
    );
    expanded.setStart(startContainer, Math.min(widened.index, range.startOffset));
  }
  if (endContainer instanceof Text) {
    const text = endContainer.data;
    const widened = expandTextEditorSelectionToWords(
      {index: 0, length: range.endOffset},
      charAtIn(text)
    );
    expanded.setEnd(endContainer, Math.max(widened.index + widened.length, range.endOffset));
  }

  return expanded;
};

const expandTextareaSelectionToWords = (value: string, start: number, end: number) => {
  const widened = expandTextEditorSelectionToWords(
    {index: start, length: end - start},
    charAtIn(value)
  );

  return {start: widened.index, end: widened.index + widened.length};
};

const FULL_PERCENTAGE = 100;
const getRectangleKey = (rect: Rectangle) =>
  [rect.top, rect.left, rect.width, rect.height].join(':');

/** Sibling decorations keep visual feedback out of the editor DOM and its serialized value. */
export function TextEditorOverlays(props: TextEditorOverlaysProps) {
  const layer = useRef<HTMLDivElement>(null);
  const toolbar = useRef<HTMLDivElement>(null);
  const cleanup = useRef<(() => void) | null>(null);
  const selectedText = useRef<string | null>(null);
  const latest = useLatest(props);
  const keptSelection = useRef<KeptSelection | null>(null);
  const [selection, setSelection] = useState<Rectangle | null>(null);
  const [highlights, setHighlights] = useState<Rectangle[]>([]);
  const [selectionHighlights, setSelectionHighlights] = useState<Rectangle[]>([]);

  // eslint-disable-next-line complexity, max-statements -- Pre-existing violation
  const update = () => {
    const editor = latest.current.getEditor();
    const frame = layer.current;
    if (isNil(editor) || isNil(frame)) {
      return;
    }

    const document = editor.ownerDocument;
    const frameBounds = frame.getBoundingClientRect();
    const inset = Number.parseFloat(window.getComputedStyle(frame).paddingTop) || 0;
    const editorBounds = editor.getBoundingClientRect();
    const horizontalRatio = frameBounds.width > 0 ? FULL_PERCENTAGE / frameBounds.width : 0;
    const verticalRatio = frameBounds.height > 0 ? FULL_PERCENTAGE / frameBounds.height : 0;
    const toRelative = (rect: Rectangle): Rectangle => ({
      left: rect.left * horizontalRatio,
      top: rect.top * verticalRatio,
      width: rect.width * horizontalRatio,
      height: rect.height * verticalRatio,
    });
    const toLocal = (rect: DOMRect): Rectangle => ({
      left: Math.max(editorBounds.left, rect.left) - frameBounds.left,
      top: Math.max(editorBounds.top, rect.top) - frameBounds.top,
      width: Math.max(
        0,
        Math.min(editorBounds.right, rect.right) - Math.max(editorBounds.left, rect.left)
      ),
      height: Math.max(
        0,
        Math.min(editorBounds.bottom, rect.bottom) - Math.max(editorBounds.top, rect.top)
      ),
    });

    const toOverlayRectangles = (rects: DOMRect[]): Rectangle[] =>
      uniqBy(
        getRectangleKey,
        mergeTextEditorRectangles(
          rects.map(toLocal).filter((rect) => rect.width > 0 && rect.height > 0)
        ).map(toRelative)
      );
    const keptSelectionRects = (): DOMRect[] => {
      const kept = keptSelection.current;
      if (isNil(kept)) {
        return [];
      }
      if (kept instanceof Range) {
        return !kept.collapsed &&
          editor.contains(kept.commonAncestorContainer) &&
          kept.getClientRects
          ? Array.from(kept.getClientRects())
          : [];
      }
      return editor instanceof HTMLTextAreaElement && kept.end > kept.start
        ? getTextareaRangeRects(editor, [{index: kept.start, length: kept.end - kept.start}])
        : [];
    };

    const requested = latest.current.editorHighlights;
    const text = editor instanceof HTMLTextAreaElement ? editor.value : (editor.textContent ?? '');
    if (isNotNil(requested) && requested.text === text) {
      const rects =
        editor instanceof HTMLTextAreaElement
          ? getTextareaRangeRects(editor, requested.ranges)
          : requested.ranges.flatMap((range) => getTextNodeRects(editor, range));
      setHighlights(toOverlayRectangles(rects));
    } else {
      setHighlights([]);
    }

    if (selectedText.current !== text) {
      selectedText.current = text;
      keptSelection.current = null;
      setSelection(null);
      setSelectionHighlights([]);
    } else if (toolbar.current?.contains(document.activeElement)) {
      // The employee is typing into the toolbar. The browser moved the native selection there,
      // so the range the editor kept is painted instead; the action still refers to it.
      setSelectionHighlights(toOverlayRectangles(keptSelectionRects()));
      return;
    }
    setSelectionHighlights([]);

    // The first and the last line of the selection: the toolbar goes above the first or below
    // the last, never across the lines in between.
    let selectedRects: DOMRect[] = [];
    if (editor instanceof HTMLTextAreaElement) {
      if (document.activeElement === editor && editor.selectionEnd > editor.selectionStart) {
        keptSelection.current = expandTextareaSelectionToWords(
          editor.value,
          editor.selectionStart,
          editor.selectionEnd
        );
        selectedRects = getTextareaRangeRects(editor, [
          {
            index: editor.selectionStart,
            length: editor.selectionEnd - editor.selectionStart,
          },
        ]);
        if (selectedRects.length === 0) {
          selectedRects = [editorBounds];
        }
      }
    } else if (editor.contains(document.activeElement)) {
      const nativeSelection = document.getSelection();
      if (
        isNotNil(nativeSelection) &&
        !nativeSelection.isCollapsed &&
        nativeSelection.rangeCount > 0
      ) {
        const range = nativeSelection.getRangeAt(0);
        if (editor.contains(range.commonAncestorContainer)) {
          keptSelection.current = expandRangeToWords(range);
          selectedRects = Array.from(range.getClientRects?.() ?? []).filter(
            (rect) => rect.width > 0 || rect.height > 0
          );
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (selectedRects.length === 0) {
            selectedRects = [range.getBoundingClientRect?.() ?? editorBounds];
          }
        }
      }
    }

    const firstRect = selectedRects.at(0);
    const lastRect = selectedRects.at(-1) ?? firstRect;

    if (
      isNil(firstRect) ||
      isNil(lastRect) ||
      lastRect.bottom < editorBounds.top ||
      firstRect.top > editorBounds.bottom
    ) {
      setSelection(null);
      return;
    }

    const toolbarWidth = toolbar.current?.offsetWidth ?? 0;
    const toolbarHeight = toolbar.current?.offsetHeight ?? 0;
    // Above the first line may cover the formatting toolbar, which is not text being read.
    // Below the last line keeps the selection and the line after it visible while typing.
    const above = firstRect.top - frameBounds.top - toolbarHeight - inset;
    const below = lastRect.bottom - frameBounds.top + inset;
    const lowestTop = frameBounds.height - toolbarHeight - inset;
    const top = match({fitsAbove: above >= inset, fitsBelow: below <= lowestTop})
      .with({fitsAbove: true}, () => above)
      .with({fitsBelow: true}, () => below)
      .otherwise(() => Math.max(inset, Math.min(above, lowestTop)));
    setSelection(
      toRelative({
        left: Math.max(
          inset,
          Math.min(firstRect.left - frameBounds.left, frameBounds.width - toolbarWidth - inset)
        ),
        top,
        width: 0,
        height: 0,
      })
    );
  };

  const updateRef = useLatest(update);
  useDeepCompareEffect(() => updateRef.current(), [props.editorHighlights]);
  useOnMount(
    () => {
      const frame = layer.current?.parentElement;
      const document = frame?.ownerDocument;
      if (isNil(frame) || isNil(document)) {
        return;
      }

      const refresh = () => updateRef.current();
      const focusSelectionAction = (event: KeyboardEvent) => {
        const editor = latest.current.getEditor();
        const nativeSelection = document.getSelection();
        const range = nativeSelection?.rangeCount ? nativeSelection.getRangeAt(0) : null;
        const hasEditorSelection =
          isNotNil(editor) &&
          (editor instanceof HTMLTextAreaElement
            ? editor.selectionEnd > editor.selectionStart
            : isNotNil(range) &&
              !range.collapsed &&
              editor.contains(range.commonAncestorContainer));
        const action = Array.from(
          toolbar.current?.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR) ?? []
        ).find((element) => {
          const style = window.getComputedStyle(element);
          return (
            element.tabIndex >= 0 &&
            !element.matches(':disabled') &&
            isNil(
              element.closest('[hidden], [inert], [aria-hidden="true"], [aria-disabled="true"]')
            ) &&
            style.display !== 'none' &&
            style.visibility !== 'hidden'
          );
        });

        if (
          event.key !== 'Tab' ||
          event.shiftKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey ||
          isNil(editor) ||
          !(event.target instanceof Node) ||
          !editor.contains(event.target) ||
          !hasEditorSelection ||
          isNil(action)
        ) {
          return;
        }

        // Capture before Quill's Tab binding can replace or indent the selected text.
        event.preventDefault();
        event.stopPropagation();
        action.focus({preventScroll: true});
      };
      frame.addEventListener('keydown', focusSelectionAction, true);
      document.addEventListener('selectionchange', refresh);
      document.addEventListener('focusin', refresh);
      document.addEventListener('keyup', refresh);
      document.addEventListener('pointerup', refresh);
      document.addEventListener('scroll', refresh, true);
      window.addEventListener('resize', refresh);
      const mutations = new MutationObserver(refresh);
      mutations.observe(frame, {childList: true, characterData: true, subtree: true});
      const resize = new ResizeObserver(refresh);
      resize.observe(frame);
      if (isNotNil(toolbar.current)) {
        resize.observe(toolbar.current);
      }
      refresh();

      cleanup.current = () => {
        frame.removeEventListener('keydown', focusSelectionAction, true);
        document.removeEventListener('selectionchange', refresh);
        document.removeEventListener('focusin', refresh);
        document.removeEventListener('keyup', refresh);
        document.removeEventListener('pointerup', refresh);
        document.removeEventListener('scroll', refresh, true);
        window.removeEventListener('resize', refresh);
        mutations.disconnect();
        resize.disconnect();
      };
    },
    () => cleanup.current?.()
  );

  return (
    <Box
      ref={layer}
      padding={1}
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      pointerEvents="none"
    >
      {highlights.map((rect) => (
        <HighlightRectangle
          key={getRectangleKey(rect)}
          $rectangle={rect}
          aria-hidden
          data-text-editor-highlight
        />
      ))}
      {selectionHighlights.map((rect) => (
        <SelectionRectangle
          key={getRectangleKey(rect)}
          $rectangle={rect}
          aria-hidden
          data-text-editor-selection-highlight
        />
      ))}
      <ToolbarPositioner ref={toolbar} $position={selection}>
        <Show when={isNotNil(selection)}>
          <div
            role="toolbar"
            aria-label={props.label ?? undefined}
            onMouseDown={(event) => event.preventDefault()}
          >
            {props.selectionContent}
          </div>
        </Show>
      </ToolbarPositioner>
    </Box>
  );
}

type RectangleProps = ComponentPropsWithoutRef<'div'> & {$rectangle: Rectangle};

const highlightRectangleVariants = cva(
  'absolute rounded-[var(--radius-small)] bg-[var(--color-palettes-purple-50-100)] opacity-20'
);

function HighlightRectangle(props: RectangleProps) {
  const {$rectangle, className, style, ...htmlProps} = props;

  return (
    <div
      {...htmlProps}
      className={cn(highlightRectangleVariants(), className)}
      style={{
        ...style,
        top: `${$rectangle.top}%`,
        left: `${$rectangle.left}%`,
        width: `${$rectangle.width}%`,
        height: `${$rectangle.height}%`,
      }}
    />
  );
}

const selectionRectangleVariants = cva(
  'absolute rounded-[var(--radius-small)] bg-[var(--color-palettes-blue-50-100)] opacity-25'
);

function SelectionRectangle(props: RectangleProps) {
  const {$rectangle, className, style, ...htmlProps} = props;

  return (
    <div
      {...htmlProps}
      className={cn(selectionRectangleVariants(), className)}
      style={{
        ...style,
        top: `${$rectangle.top}%`,
        left: `${$rectangle.left}%`,
        width: `${$rectangle.width}%`,
        height: `${$rectangle.height}%`,
      }}
    />
  );
}

type ToolbarPositionerProps = ComponentPropsWithoutRef<'div'> & {
  $position: Rectangle | null;
};

const toolbarPositionerVariants = cva('absolute z-[var(--z-content)] max-w-[calc(100%_-_0.5rem)]');

const ToolbarPositioner = forwardRef(ToolbarPositionerComponent);
function ToolbarPositionerComponent(
  props: ToolbarPositionerProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {$position, className, style, ...htmlProps} = props;

  return (
    <div
      {...htmlProps}
      ref={ref}
      className={cn(toolbarPositionerVariants(), className)}
      style={{
        ...style,
        top: `${$position?.top ?? 0}%`,
        left: `${$position?.left ?? 0}%`,
        pointerEvents: isNotNil($position) ? 'auto' : 'none',
      }}
    />
  );
}
